import { noop } from 'lodash';
import { useCallback } from 'react';

import { ICD9Procedure, ICD9ProcedureQuery } from '@dreryk/egabinet-icd-dictionary-frontend-api';

import { CqrsQueryPerformResult, performCqrsQuerySearch } from '@dreryk/command-query-react-common';
import {
   Autosuggest,
   StrongText,
   Text,
   TextSeparator,
   TextSeparatorType,
   useInteractiveTableContext,
} from '@dreryk/react-components';
import { TextPattern } from '@dreryk/watermelon';

import { visitDetailsTranslationKeys } from '../../../../translations';
import { VisitFormController } from '../../visitForm.types';

import { ProcedureTableBean } from './procedures.types';

const icd9ProceduresSearchCreator = (dictionaryValidOn: Date) => {
   return (searchText: string | null) => {
      return async (): CqrsQueryPerformResult<ICD9Procedure[]> =>
         performCqrsQuerySearch<ICD9Procedure>(() => {
            const query = new ICD9ProcedureQuery();
            query.codeOrName = TextPattern.anywhereIgnoreCase(searchText ?? undefined);
            query.dictionaryValidOn = dictionaryValidOn;
            return query;
         });
   };
};

const labelExtractor = (): string => {
   // we don't to display anything after selection
   return '';
};

const itemRenderer = (item: ICD9Procedure): JSX.Element => {
   return (
      <TextSeparator separator={TextSeparatorType.SPACE}>
         <StrongText testId={'name'}>{item.name}</StrongText>
         <Text testId={'code'}>{`(${item.code})`}</Text>
      </TextSeparator>
   );
};

type ProceduresTableSelectorProps = Pick<VisitFormController, 'register'> & {
   readonly dictionaryValidOn: Date;
};

export const ProceduresTableSelector = (props: ProceduresTableSelectorProps): JSX.Element => {
   const { addRow, getCellValue, setCellValue, getValues } = useInteractiveTableContext<ProcedureTableBean>();

   const handleSelectionChange = useCallback(
      (selectedProcedure: ICD9Procedure | null): void => {
         if (!selectedProcedure) {
            return noop();
         }

         const existingProcedureIndex = getValues().findIndex((value) => selectedProcedure.code === value.code);

         if (existingProcedureIndex === -1) {
            return addRow(
               {
                  code: selectedProcedure.code,
                  name: selectedProcedure.name,
                  quantity: 1,
                  minQuantity: 0,
               },
               'prepend'
            );
         }

         const currentQuantityValue: number = getCellValue('quantity', existingProcedureIndex);
         const incrementedQuantityValue = currentQuantityValue + 1;
         return setCellValue('quantity', existingProcedureIndex, incrementedQuantityValue);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [getValues, getCellValue, setCellValue]
   );

   return (
      <Autosuggest<ICD9Procedure>
         placeholder={visitDetailsTranslationKeys.procedures.selectorPlaceholder}
         horizontal={false}
         controller={props.register('procedures.search')}
         searchCreator={icd9ProceduresSearchCreator(props.dictionaryValidOn)}
         itemRenderer={itemRenderer}
         onSelectionChange={handleSelectionChange}
         labelExtractor={labelExtractor}
         keyProperty={'code'}
         minChars={2}
         testId={'procedureSelector'}
         resetAfterSelection
      />
   );
};
